import { useSettings } from '../contexts/SettingsContext';

export function HelpPage() {
  const { pickupEnabled } = useSettings();

  const bestellablaufSteps = [
    {
      title: 'Speiseplan ansehen',
      text: 'Ab Mittwoch (12:00 Uhr) siehst du die Gerichte für die kommende Woche. Vorher ist die Woche als gesperrt gekennzeichnet.',
    },
    {
      title: 'Gericht auswählen',
      text: 'Klicke auf ein Gericht eines Tages — es öffnet sich ein Fenster wo du bestätigst, für wen du bestellst und deinen Abholcode festlegst (beim ersten Mal).',
    },
    ...(pickupEnabled ? [{
      title: 'Abholcode festlegen',
      text: 'Du legst einmalig deinen persönlichen Abholcode fest. Das Format ist: Ziffer-Buchstabe-Ziffer-Buchstabe, z.B. 6F3B. Merke dir diesen Code diesen benötigst du bei der Abholung. Du kannst ihn später über dein Profil ändern.',
      highlight: '6F3B',
    }] : []),
    {
      title: 'Bestellung abschicken',
      text: 'Wenn du alle Gerichte im Warenkorb hast, klickst du auf „Bestellung abschicken"',
    },
    ...(pickupEnabled ? [{
      title: 'Essen abholen',
      text: 'Bei der Ausgabe gibst du den 4-stelligen Code ein. Dann siehst du deine Bestellung für den Tag. Der Code lässt sich nur einmal pro Tag verwenden.',
    }] : []),
  ];

  return (
    <div style={{ maxWidth: '48rem', margin: '0 auto', padding: '2rem 1.5rem' }}>

      <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
        Hilfe & Erklärungen
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '2.5rem' }}>
        Hier findest du alle wichtigen Infos rund um Foody Leipzig.
      </p>

      <Section title="Was ist Foody Leipzig?">
        <p>
          Foody Leipzig ist die digitale Bestellplattform für die Essensbestellung am Standort Leipzig.
          Hier kannst du dein Mittagessen für die Woche planen, bestellen und stornieren für dich und für Kolleg:innen oder Gäste.
          {pickupEnabled && ' Hier verwaltest Du auch deinen persönlichen Abholcode für die Abholung in der Kantine.'}
        </p>
      </Section>

      <Section title="Wie läuft eine Bestellung ab?">
        <Steps items={bestellablaufSteps} />
      </Section>

      <Section title="Fristen — wann kann ich bestellen und stornieren?">
        <p style={{ marginBottom: '1rem' }}>
          Bestellungen und Stornierungen sind bis zum jeweiligen Bestellschluss möglich. Bestellschluss und Stonierungsschluss ist immer 2 Tage im vorraus bis 12:00 Uhr. Beachte!, Stornierungen sollen die Ausnahme z.B. bei Krankheit sein.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.813rem' }}>
          <thead>
            <tr style={{ background: 'var(--surface)' }}>
              {['Essen am', 'Bestellschluss', 'Stornierungsfrist'].map(h => (
                <th key={h} style={{ padding: '0.625rem 0.875rem', textAlign: 'left', fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.688rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Montag', 'Mittwoch Vorwoche, 12:00 Uhr', 'Mittwoch Vorwoche, 12:00 Uhr'],
              ['Dienstag', 'Donnerstag Vorwoche, 12:00 Uhr', 'Donnerstag Vorwoche, 12:00 Uhr'],
              ['Mittwoch', 'Montag, 12:00 Uhr', 'Montag, 12:00 Uhr'],
              ['Donnerstag', 'Dienstag, 12:00 Uhr', 'Dienstag, 12:00 Uhr'],
              ['Freitag', 'Mittwoch, 12:00 Uhr', 'Mittwoch, 12:00 Uhr'],
            ].map(([day, deadline, cancel], i) => (
              <tr key={day} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--surface)' }}>
                <td style={{ padding: '0.625rem 0.875rem', fontWeight: 600 }}>{day}</td>
                <td style={{ padding: '0.625rem 0.875rem' }}>{deadline}</td>
                <td style={{ padding: '0.625rem 0.875rem' }}>{cancel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="Für Kolleg:innen bestellen">
        <p>
          Du kannst auch für Kolleg:innen aus deinem Unternehmen mitbestellen. Wähle beim Bestellen
          „Kolleg:in" und suche nach dem Namen — die Suche greift direkt auf das Firmenverzeichnis zu. Du kannst nur Kollegen aus deinem Unternehmen auswählen.
          {pickupEnabled && ' Der Abholcode deiner Kollegin / deines Kollegen wird für die Abholung genutzt. Deine Kollegin / dein Kollege muss daher ebenfalls einen Abholcode in der App hinterlegt haben.'}
        </p>
      </Section>

      {pickupEnabled && (
        <Section title="Für Gäste bestellen">
          <p>
            Du kannst ein Mittagessen für einen Gast mitbestellen. Gib den Namen des Gastes ein.
            Der Abholcode für den Gast wird automatisch erzeugt und nach dem Bestellen angezeigt;
            du findest ihn auch unter „Meine Bestellungen". Teile den Code dem Gast mit, damit er/sie
            das Essen an der Ausgabe abholen kann. Der Code gilt nur für den Bestelltag.
          </p>
        </Section>
      )}

      <Section title="Meine Bestellungen">
        <p>
          Unter „Meine Bestellungen" siehst du alle deine aktuellen und zukünftigen Bestellungen.
          Solange die Frist noch nicht abgelaufen ist, kannst du eine Bestellung noch ändern oder stornieren.
        </p>
      </Section>

      <Section title="Was passiert wenn kein Speiseplan da ist?">
        <p>
          Falls für einen Tag kein Speiseplan eingetragen wurde, siehst du „Kein Speiseplan für
          diesen Tag". Dies passiert z.B. bei Feiertagen. In diesem Fall kannst du für diesen Tag nichts bestellen.
        </p>
        <InfoBox>
          Bei generellen Problemen wende dich bitte an den Frontdesk in Leipzig. Bei Störungen öffne bitte ein Ticket über
          den Servicedesk oder die Hotline.
        </InfoBox>
      </Section>

    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '2rem', background: 'var(--card-bg)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)', padding: '1.5rem 1.75rem' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '1rem' }}>{title}</h2>
      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.7 }}>
        {children}
      </div>
    </div>
  );
}

function Steps({ items }: { items: { title: string; text: string; highlight?: string }[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <div style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', background: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.813rem', flexShrink: 0, marginTop: '0.125rem' }}>
            {i + 1}
          </div>
          <div>
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{item.title}</div>
            <div style={{ color: 'var(--text-muted)' }}>{item.text}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: '0.875rem', background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: '0.75rem', padding: '0.75rem 1rem', fontSize: '0.813rem', lineHeight: 1.6 }}>
      {children}
    </div>
  );
}
